<template>
  <form
    role="search"
    aria-label="Search"
    style="width: 100% !important;"
  >
    <v-skeleton-loader
      v-if="isLoadingInput"
      type="input"
      class="pa-0 ma-0"
    />

    <v-menu
      v-else
      v-model="expanded"
      :content-class="searchContainerClass"
      offset-y
      :close-on-content-click="false"
      allow-overflow
      nudge-bottom="6"
    >
      <template v-slot:activator="{ props }">
        <v-text-field
          v-model="userInput"
          v-bind="props"
          autocomplete="off"
          class="mt-2 font-weight-bold"
          dense
          flat
          hide-details
          :label="inputLabel"
          :loading="isLoadingSearch"
          :placeholder="inputLabel"
          :search-input.sync="userInput"
          :single-line="true"
          @keydown.up="onArrowUp()"
          @keydown.down="onArrowDown()"
          @input="searchItems(userInput)"
        />
      </template>

      <v-card
        class="search-results"
        max-height="390px"
      >
        <v-container
          class="pa-0 ma-0"
          fluid
        >
          <v-list
            v-for="endpoint in searchEndpoints"
            ref="search-list"
            :key="endpoint.baseReportName"
            class="py-0 very-dense search-results-list"
          >
            <v-subheader
              v-if="showHeaders && endpoint.results && endpoint.results.length"
              :class="`search-header ${titleColor}--text px-3`"
            >
              <div>
                {{ endpoint.name }}
              </div>

              <v-spacer />

              <div v-if="endpoint.error">
                {{ endpoint.error.code }} Error
              </div>

              <div
                v-else-if="endpoint.results && endpoint.results.length"
                :class="`${endpoint.results && endpoint.results.length ? 'cursor-pointer font-weight-bold' : `${textColor}--text`}`"
                @click="toggleAllResults(endpoint.reportKey)"
              >
                {{ endpoint.results.length > 1 ? 'Show All' : '' }} {{ endpoint.results.length }} {{ endpoint.results.length === 1 ? 'Result' : 'Results' }}
              </div>
            </v-subheader>

            <v-list-item
              v-for="(item, itemIndex) in mySearchResults[endpoint.reportKey]"
              :key="`${endpoint.reportKey}-${itemIndex}`"
              :aria-label="item[textKey]"
              @click="selectSearchItem(item)"
            >
              <v-list-item-content
                class="search-result pa-0"
              >
                <v-list-item-title
                  class="body-2 pa-0"
                >
                  <span>{{ item[textKey] }}</span>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-container>
      </v-card>
    </v-menu>

    <span
      v-if="didLoadSearchFail && searchModel"
      class="error-message"
    >
      <v-icon>error_outline</v-icon>
      Failed to search. Please try again later.
    </span>
  </form>
</template>

<script>
import Fuse from 'fuse.js'
import { createNamespacedHelpers } from 'vuex'

import render from 'src/utils/render'

const { mapGetters: mapAuthGetters, mapState: mapAuthState } = createNamespacedHelpers('auth')
const { mapGetters: mapLayoutGetters } = createNamespacedHelpers('layout')
const { mapActions: mapToolsActions, mapState: mapToolsState } = createNamespacedHelpers('tools')

export default {
  name: 'SearchSimple',
  props: {
    endpointExcludes: {
      type: Array,
      default: () => [],
    },
    endpointName: {
      type: String,
      default: 'all',
    },
    eventName: {
      type: String,
      default: null,
    },
    inputLabel: {
      type: String,
      default: 'Search',
    },
    reportCodeIncludes: {
      type: Array,
      default: () => [],
      required: false,
    },
    selectedItem: {
      type: Object,
      default: () => ({}),
    },
    showAllResults: {
      type: Boolean,
      default: true,
    },
    showHeaders: {
      type: Boolean,
      default: true,
    },
    textKey: {
      type: String,
      default: 'text',
    },
    valueKey: {
      type: String,
      default: 'value',
    },
  },
  data: () => ({
    searchLabelDefault: 'Search ...',
    searchLabelFailed: 'Try another search ...',
    searchResults: { endpoints: {}, total: {} },
    showingAllResults: false,
    expanded: false,
    ready: false,
    seeNetworks: false,
    seeAllResults: false,
    showSearchPreferences: false,
    userInput: '',
  }),
  computed: {
    ...mapAuthGetters(['inDarkMode', 'inDemoMode', 'isInternalUser', 'maxSearchResults', 'network']),
    ...mapAuthState({
      user: (state) => state.user,
    }),
    ...mapLayoutGetters(['textColor', 'titleColor']),
    ...mapToolsState({
      didLoadSearchFail: (state) => state.didLoadSearchFail,
      isLoadingSearch: (state) => state.isLoadingSearch,
      $searchResults: (state) => state.searchResults,
      SEARCH_OPTIONS: (state) => state.SEARCH_OPTIONS,
    }),
    isShowingAllResults: {
      get() {
        return this.showAllResults || this.showingAllResults
      },
      set(value) {
        this.showingAllResults = value
      },
    },
    isLoadingInput() {
      return this.isLoadingSearch || !this.$searchResults || !this.$searchResults.endpoints || !Object.keys(this.$searchResults.endpoints).length
    },
    allowedEndpoints() {
      const { endpointName, searchResults: { endpoints: allowedEndpoints } } = this
      const allowedEndpointKeys = allowedEndpoints && Object.keys(allowedEndpoints)
      const haveAllowedEndpoints = allowedEndpointKeys && !!allowedEndpointKeys.length
      const reduceExcluded = (endpoints, endpointKey) => {
        const endpoint = allowedEndpoints[endpointKey]
        const navKey = endpoint.baseReportName || endpoint.routeName
        const isIncluded = this.endpointExcludes.indexOf(navKey) === -1

        if (isIncluded) {
          endpoints[endpointKey] = endpoint
        }

        return endpoints
      }
      const getAllEndpoints = () => endpointName === 'all' && allowedEndpointKeys.reduce(reduceExcluded, {})
      const getSpecificEndpoint = () => endpointName && allowedEndpoints[endpointName] && {
        [endpointName]: allowedEndpoints[endpointName],
      }
      const getEndpoints = () => haveAllowedEndpoints && (getAllEndpoints() || getSpecificEndpoint())

      return getEndpoints() || {}
    },
    allowedSearchOptionsByEndpoint() {
      const { allowedEndpoints, reportCodeIncludes, SEARCH_OPTIONS } = this
      const endpointNames = Object.keys(allowedEndpoints)
      const getFilteredEndpointOptions = () => reportCodeIncludes && reportCodeIncludes.length && endpointNames.reduce((newSearchEndpoints, endpointName) => {
        const endpoint = allowedEndpoints[endpointName]
        const filterByReportCode = (item) => reportCodeIncludes.find((reportCode) => reportCode.associationId === item.associationId && reportCode.subId === item.subId)
        const itemsArray = (endpoint.itemsArray && endpoint.itemsArray.filter(filterByReportCode)) || []
        const items = new Fuse(itemsArray, SEARCH_OPTIONS)

        newSearchEndpoints[endpointName] = { ...endpoint, items, itemsArray }

        return newSearchEndpoints
      }, {})

      return getFilteredEndpointOptions() || allowedEndpoints
    },
    searchOptions() {
      const { allowedSearchOptionsByEndpoint } = this
      const getSearchOptions = () => {
        const endpointNames = Object.keys(allowedSearchOptionsByEndpoint)
        const total = this.searchTotal
        return { endpointNames, endpoints: allowedSearchOptionsByEndpoint, total }
      }
      const getDefaultSearchOptions = () => {
        const endpointNames = []
        const endpoints = {}
        const total = 0
        return { endpointNames, endpoints, total }
      }

      return (allowedSearchOptionsByEndpoint && getSearchOptions()) || getDefaultSearchOptions()
    },
    selectedSearchResult() {
      const { allowedSearchOptionsByEndpoint, selectedItem } = this
      const getSelectedSearchOption = () => {
        const { associationId, baseReportName, reportPath, routeName, subId } = selectedItem
        const endpointKey = baseReportName || routeName
        const { itemsArray } = (allowedSearchOptionsByEndpoint[endpointKey] && allowedSearchOptionsByEndpoint[endpointKey]) || {}

        return itemsArray && itemsArray.find((result) => {
          const { associationId: resultAssociationId, reportPath: resultReportPath, routeName: resultRouteName, subId: resultSubId } = result
          return routeName === resultRouteName && associationId === resultAssociationId && reportPath === resultReportPath && subId === resultSubId
        })
      }

      return allowedSearchOptionsByEndpoint && selectedItem && getSelectedSearchOption()
    },
    searchText() {
      return (this.selectedSearchResult && this.selectedSearchResult[this.textKey]) || ''
    },
    searchModel: {
      get() {
        return this.searchText
      },
      set(selectedReport) {
        if (this.eventName) {
          this.$store.dispatch(this.eventName, {
            selectedItem: this.selectedItem,
            selectedReport,
          })
        }
      },
    },
    mySearchResults() {
      const { allowedSearchOptionsByEndpoint, isShowingAllResults, userInput } = this
      const endpointNames = Object.keys(allowedSearchOptionsByEndpoint)

      return endpointNames.reduce((endpoints, endpointName) => {
        const allowedEndpoint = allowedSearchOptionsByEndpoint[endpointName]

        if (userInput) {
          endpoints[endpointName] = isShowingAllResults ? allowedEndpoint.results : allowedEndpoint.results.slice(0, this.maxSearchResults)
        } else {
          const hasItemsArray = isShowingAllResults || !allowedEndpoint.itemsArray
          endpoints[endpointName] = hasItemsArray ? allowedEndpoint.itemsArray : allowedEndpoint.itemsArray.slice(0, this.maxSearchResults)
        }

        return endpoints
      }, {})
    },
    searchEndpoints() {
      const { allowedSearchOptionsByEndpoint } = this
      const getEndpointsWithResults = () => Object.keys(allowedSearchOptionsByEndpoint).reduce((newEndpoints, endpointKey) => {
        const endpoint = allowedSearchOptionsByEndpoint[endpointKey]
        const hasResults = endpoint.results && endpoint.results.length
        if (hasResults) {
          endpoint.reportKey = endpoint.baseReportName || endpoint.routeName
          newEndpoints[endpointKey] = endpoint
        }
        return newEndpoints
      }, {})
      return (this.userInput && getEndpointsWithResults()) || allowedSearchOptionsByEndpoint
    },
    searchTotal() {
      return this.searchResults.total
    },
    searchLabel() {
      const { didLoadSearchFail, isLoadingSearch, selectedItem } = this

      if (!selectedItem) return this.searchLabelDefault

      if (isLoadingSearch) return 'Loading options ...'

      if (didLoadSearchFail) return this.searchLabelFailed

      return this.searchText
    },
    showResults() {
      const { didLoadSearchFail, isLoadingSearch } = this
      const isNotLoading = isLoadingSearch === false
      const didNotFail = didLoadSearchFail === false

      return isNotLoading && didNotFail
    },
    searchContainerClass() {
      const { menuClass, searchTotal, userInput } = this
      const results = searchTotal?.results > 0
      const recentSearches = searchTotal?.recent > 0
      const haveContent = results || recentSearches || userInput

      const baseClasses = 'search-results-container white'
      const menuClasses = menuClass ? ` ${menuClass}` : ''
      const resultClasses = haveContent ? '' : ' results-empty'
      const containerClass = `${baseClasses}${menuClasses}${resultClasses}`

      return containerClass
    },
  },
  watch: {
    didLoadSearchFail(newValue) {
      if (newValue) {
        this.expanded = false
      }
    },
    inDemoMode() {
      this.setup()
    },
  },
  created() {
    this.setup()
  },
  methods: {
    ...mapToolsActions([
      'getSearchSimple',
    ]),
    async setup() {
      this.userInput = this.searchText
      this.expanded = false

      const { $searchResults: searchOptions } = this
      const searchTerm = (this.selectedItem && this.selectedItem[this.valueKey]) || ''
      const { textKey } = this
      const hasSearchStarted = false

      this.searchResults = await this.getSearchSimple({ hasSearchStarted, searchOptions, searchTerm, textKey })

      this.ready = true
    },

    onArrowUp() {
      render.keepHighlightedElementInScrollView.call(this)
    },
    onArrowDown() {
      render.keepHighlightedElementInScrollView.call(this)
    },

    isCurrentSelection(selection) {
      const { associationId, baseReportName, reportPath, routeName, subId } = selection
      const {
        associationId: currentAssociationId,
        baseReportName: currentBaseReportName,
        reportPath: currentQueryPath,
        routeName: currentRouteName,
        subId: currentSubId,
      } = this.selectedItem || {}

      return currentQueryPath === reportPath
          && currentAssociationId === String(associationId, `The selected item's association ID is not a string: ${associationId}`)
          && currentBaseReportName === baseReportName
          && currentRouteName === routeName
          && (currentSubId === subId || (!subId && currentSubId === 'all-secondary'))
    },
    selectSearchItem(selection) {
      this.expanded = false
      const selectionLabel = selection[this.textKey]

      if (this.isCurrentSelection(selection)) {
        const message = `<b>Already selected</b><br>${selectionLabel}`

        this.$store.commit('tools/snackbar', true)
        this.$store.commit('tools/snack', { message, status: 'info' })
      } else {
        this.searchModel = selection
        this.userInput = selectionLabel
      }
    },

    async searchItems(searchTerm) {
      const { searchOptions, textKey } = this

      if (this.ready) {
        this.expanded = true
        const hasSearchStarted = true
        this.searchResults = await this.$store.dispatch('tools/getSearchSimple', { hasSearchStarted, searchOptions, searchTerm, textKey })
      }
    },

    toggleAllResults(endpointName) {
      if (this.showingAllResults === endpointName) {
        this.showingAllResults = false
      } else {
        this.showingAllResults = endpointName
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~src/styles/variables";

.search-results-container {
  border-radius: 6px;
  box-shadow: 0 2px 6px 3px rgba(165, 177, 185, 0.3);
}

.search-header {
  text-transform: uppercase;
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 0.26px;
  line-height: 11px;
}

.search-results {
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 3ch);
}

.search-results-list {
  flex-grow: 1;
  flex-shrink: 1;
  flex-basis: auto;
  overflow: auto;
}

.recents-list {
  flex-basis: auto;
  flex-grow: 0;
  flex-shrink: 0;
}

::v-deep.v-input__icon i {
  color: $color-monochrome-darkestblue;
}

@media screen and (max-width: $vuetify-md-max-size) {
  .search-results {
    max-height: calc(100vh - 9ch);
  }
}

.v-list-item {
  min-height: 36px;
}

</style>
